import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    emptyMediaKitSocialFormState,
    inferMediaKitWizardResumeStep,
    mapMediaKitToFormState,
    mediaKitWizardStepStorageKey,
} from '../utils/mediaKitFormUtils';
import {
    normalizeAtHandlesInSocials,
    validateFacebookName,
    validateHudlUrl,
    validateInstagramHandle,
    validateLinkedInUrl,
    validateTikTokHandle,
    validateXHandle,
} from '../utils/socialMediaUtils';
import {
    MEDIA_KIT_MAX_ABOUT_CHARS,
    MEDIA_KIT_MAX_HIGHLIGHT_DESCRIPTION_CHARS,
    MEDIA_KIT_MAX_HIGHLIGHT_IMPACT_CHARS,
    MEDIA_KIT_MAX_HIGHLIGHT_TITLE_CHARS,
    MEDIA_KIT_MAX_PHOTOS,
    truncateMediaKitField,
    validateMediaKitAbout,
    validateMediaKitGraduationYear,
    validateMediaKitHighlightFields,
} from '../utils/mediaKitValidation';
import UploadImageModal from './UploadImageModal';
import MediaKitSocialFields from './MediaKitSocialFields';
import {
    MediaKitData,
    InterestCategory,
    addMediaKitHighlight,
    buildMediaKitPhotoUploadUrl,
    completeMediaKit,
    deleteMediaKitPhoto,
    fetchInterestCatalog,
    fetchMediaKit,
    hasHighlightFields,
    isValidIntroVideoUrl,
    saveMediaKitAbout,
    saveMediaKitBasics,
    saveMediaKitIntroVideo,
    saveMediaKitInterests,
    saveMediaKitSocials,
} from '../services/mediaKitService';
import mediaKitLandingPreview from '../images/media-kit/media-kit-landing-preview.png';
import polarisStarLogo from '../images/media-kit/polaris-star-logo.svg';
import '../styles/media-kit.css';

type WizardStep = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;

interface MediaKitWizardProps {
    athleteUserId?: number;
    initialData?: MediaKitData | null;
    onComplete: (data: MediaKitData) => void;
}

export default function MediaKitWizard({ athleteUserId, initialData, onComplete }: MediaKitWizardProps) {
    const stepStorageKey = mediaKitWizardStepStorageKey(athleteUserId);
    const [step, setStep] = useState<WizardStep>(() => {
        try {
            const raw = sessionStorage.getItem(mediaKitWizardStepStorageKey(athleteUserId));
            const saved = raw != null ? Number(raw) : NaN;
            if (Number.isInteger(saved) && saved >= 1 && saved <= 8) {
                return saved as WizardStep;
            }
        } catch {
            /* sessionStorage unavailable */
        }
        if (initialData && !initialData.completed) {
            const inferred = inferMediaKitWizardResumeStep(initialData);
            if (inferred >= 1 && inferred <= 8) {
                return inferred as WizardStep;
            }
        }
        return 0;
    });
    const [data, setData] = useState<MediaKitData | null>(initialData ?? null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [graduationYear, setGraduationYear] = useState('');
    const [aboutYou, setAboutYou] = useState('');
    const [socials, setSocials] = useState(emptyMediaKitSocialFormState);
    const [socialErrors, setSocialErrors] = useState<Record<string, string>>({});
    const [interestCategories, setInterestCategories] = useState<InterestCategory[]>([]);
    const [interestsLoading, setInterestsLoading] = useState(true);
    const [selectedInterestIds, setSelectedInterestIds] = useState<number[]>([]);
    const [customInterests, setCustomInterests] = useState('');
    const [activeCategoryIndex, setActiveCategoryIndex] = useState(0);
    const [introVideoUrl, setIntroVideoUrl] = useState('');
    const [introVideoError, setIntroVideoError] = useState<string | null>(null);
    const [highlightTitle, setHighlightTitle] = useState('');
    const [highlightDescription, setHighlightDescription] = useState('');
    const [highlightImpact, setHighlightImpact] = useState('');
    const [additionalTitle, setAdditionalTitle] = useState('');
    const [additionalDescription, setAdditionalDescription] = useState('');
    const [additionalImpact, setAdditionalImpact] = useState('');
    const [showUploadModal, setShowUploadModal] = useState(false);

    const interestsPrefilled = useRef(false);
    const formHydratedRef = useRef(false);
    const resumeAppliedRef = useRef(false);

    const applyKitToForm = useCallback((kit: MediaKitData) => {
        setData(kit);
        const form = mapMediaKitToFormState(kit);
        setGraduationYear(form.graduationYear);
        setAboutYou(form.aboutYou);
        setSocials(form.socials);
        setIntroVideoUrl(form.introVideoUrl);
        setCustomInterests(form.customInterests);
        interestsPrefilled.current = false;
    }, []);

    const loadData = useCallback(async () => {
        try {
            const kit = await fetchMediaKit(athleteUserId);
            applyKitToForm(kit);
            formHydratedRef.current = true;
            if (!resumeAppliedRef.current && !kit.completed) {
                resumeAppliedRef.current = true;
                try {
                    const raw = sessionStorage.getItem(stepStorageKey);
                    const saved = raw != null ? Number(raw) : NaN;
                    if (Number.isInteger(saved) && saved >= 1 && saved <= 8) {
                        setStep(saved as WizardStep);
                    } else {
                        const inferred = inferMediaKitWizardResumeStep(kit);
                        if (inferred >= 1) {
                            setStep(inferred as WizardStep);
                        }
                    }
                } catch {
                    const inferred = inferMediaKitWizardResumeStep(kit);
                    if (inferred >= 1) {
                        setStep(inferred as WizardStep);
                    }
                }
            }
        } catch (e: unknown) {
            setError(e instanceof Error ? e.message : 'Failed to load media kit');
        }
    }, [athleteUserId, applyKitToForm, stepStorageKey]);

    useEffect(() => {
        if (!initialData) {
            loadData();
            return;
        }
        // Pre-populate form from profile-backed API data; re-hydrate on landing only.
        if (!formHydratedRef.current || step === 0) {
            applyKitToForm(initialData);
            formHydratedRef.current = true;
            return;
        }
        setData(initialData);
    }, [initialData, step, applyKitToForm, loadData]);

    useEffect(() => {
        if (step < 1) {
            return;
        }
        try {
            sessionStorage.setItem(stepStorageKey, String(step));
        } catch {
            /* ignore */
        }
    }, [step, stepStorageKey]);

    useEffect(() => {
        let cancelled = false;

        const loadInterestCatalog = async () => {
            setInterestsLoading(true);
            try {
                const catalog = await fetchInterestCatalog();
                if (!cancelled) {
                    setInterestCategories(catalog);
                }
            } catch {
                if (!cancelled) {
                    setError('Failed to load interests');
                }
            } finally {
                if (!cancelled) {
                    setInterestsLoading(false);
                }
            }
        };

        loadInterestCatalog();
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        if (interestsPrefilled.current || !interestCategories.length || !data?.selectedInterests?.length) {
            return;
        }

        const interestIdByName = new Map(
            interestCategories.flatMap((category) =>
                category.interests.map((interest) => [interest.name.toLowerCase(), interest.id] as const)
            )
        );
        const existingIds = data.selectedInterests
            .map((name) => interestIdByName.get(name.toLowerCase()))
            .filter((id): id is number => id != null);

        if (existingIds.length) {
            setSelectedInterestIds(existingIds);
        }
        interestsPrefilled.current = true;
    }, [interestCategories, data?.selectedInterests]);

    const refreshData = async () => {
        const kit = await fetchMediaKit(athleteUserId);
        // Photos only — do not rehydrate form drafts (about, socials, etc.) from the server.
        setData(kit);
        return kit;
    };

    const handleGetStarted = () => setStep(1);

    const handleBack = () => {
        if (loading || step <= 0) {
            return;
        }
        setError(null);
        setIntroVideoError(null);
        setSocialErrors({});
        setStep((current) => (current - 1) as WizardStep);
    };

    const handleNextBasics = async () => {
        setError(null);
        if (!data?.graduationYear) {
            const graduationError = validateMediaKitGraduationYear(graduationYear);
            if (graduationError) {
                setError(graduationError);
                return;
            }
        }
        setLoading(true);
        try {
            if (!data?.graduationYear && graduationYear.trim()) {
                await saveMediaKitBasics(parseInt(graduationYear, 10), athleteUserId);
                await refreshData();
            }
            setStep(2);
        } catch (e: unknown) {
            setError(e instanceof Error ? e.message : 'Failed to save graduation year');
        } finally {
            setLoading(false);
        }
    };

    const handleNextAbout = async () => {
        const aboutError = validateMediaKitAbout(aboutYou);
        if (aboutError) {
            setError(aboutError);
            return;
        }
        setLoading(true);
        setError(null);
        try {
            await saveMediaKitAbout(aboutYou, athleteUserId);
            await refreshData();
            setStep(3);
        } catch (e: unknown) {
            setError(e instanceof Error ? e.message : 'Failed to save');
        } finally {
            setLoading(false);
        }
    };

    const handleNextPhotos = () => setStep(4);

    const handlePhotoUploaded = async () => {
        setShowUploadModal(false);
        await refreshData();
    };

    const handleDeletePhoto = async (photoId: number) => {
        await deleteMediaKitPhoto(photoId, athleteUserId);
        await refreshData();
    };

    const validateSocials = (): boolean => {
        const errors: Record<string, string> = {};
        const checks = [
            ['instagramHandle', validateInstagramHandle(socials.instagramHandle)],
            ['tiktokHandle', validateTikTokHandle(socials.tiktokHandle)],
            ['linkedinUrl', validateLinkedInUrl(socials.linkedinUrl)],
            ['facebookName', validateFacebookName(socials.facebookName)],
            ['hudlUrl', validateHudlUrl(socials.hudlUrl)],
            ['xHandle', validateXHandle(socials.xHandle)],
        ] as const;
        checks.forEach(([key, result]) => {
            if (!result.isValid && result.errorMessage) {
                errors[key] = result.errorMessage;
            }
        });
        setSocialErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleNextSocials = async () => {
        if (!validateSocials()) return;
        setLoading(true);
        setError(null);
        try {
            await saveMediaKitSocials(normalizeAtHandlesInSocials(socials), athleteUserId);
            await refreshData();
            setStep(5);
        } catch (e: unknown) {
            setError(e instanceof Error ? e.message : 'Failed to save socials');
        } finally {
            setLoading(false);
        }
    };

    const toggleInterest = (id: number) => {
        setSelectedInterestIds((prev) =>
            prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
        );
    };

    const handleNextInterests = async () => {
        setLoading(true);
        setError(null);
        try {
            await saveMediaKitInterests(selectedInterestIds, customInterests, athleteUserId);
            setStep(6);
        } catch (e: unknown) {
            setError(e instanceof Error ? e.message : 'Failed to save interests');
        } finally {
            setLoading(false);
        }
    };

    const handleNextIntroVideo = async () => {
        if (introVideoUrl.trim() && !isValidIntroVideoUrl(introVideoUrl)) {
            setIntroVideoError('Please enter a valid YouTube or Vimeo URL.');
            return;
        }
        setIntroVideoError(null);
        setLoading(true);
        setError(null);
        try {
            await saveMediaKitIntroVideo(introVideoUrl, athleteUserId);
            setStep(7);
        } catch (e: unknown) {
            setError(e instanceof Error ? e.message : 'Failed to save intro video');
        } finally {
            setLoading(false);
        }
    };

    const handleNextHighlight = async () => {
        if (hasHighlightFields(highlightTitle, highlightDescription, highlightImpact)) {
            const highlightError = validateMediaKitHighlightFields(
                highlightTitle,
                highlightDescription,
                highlightImpact,
            );
            if (highlightError) {
                setError(highlightError);
                return;
            }
        }
        setLoading(true);
        setError(null);
        try {
            if (hasHighlightFields(highlightTitle, highlightDescription, highlightImpact)) {
                await addMediaKitHighlight(
                    { title: highlightTitle, description: highlightDescription, impact: highlightImpact },
                    athleteUserId
                );
            }
            setStep(8);
        } catch (e: unknown) {
            setError(e instanceof Error ? e.message : 'Failed to save highlight');
        } finally {
            setLoading(false);
        }
    };

    const finishMediaKit = async () => {
        const completed = await completeMediaKit(athleteUserId);
        try {
            sessionStorage.removeItem(stepStorageKey);
        } catch {
            /* ignore */
        }
        onComplete(completed);
    };

    const handleComplete = async () => {
        if (hasHighlightFields(additionalTitle, additionalDescription, additionalImpact)) {
            const highlightError = validateMediaKitHighlightFields(
                additionalTitle,
                additionalDescription,
                additionalImpact,
            );
            if (highlightError) {
                setError(highlightError);
                return;
            }
        }
        setLoading(true);
        setError(null);
        try {
            if (hasHighlightFields(additionalTitle, additionalDescription, additionalImpact)) {
                await addMediaKitHighlight(
                    { title: additionalTitle, description: additionalDescription, impact: additionalImpact },
                    athleteUserId
                );
            }
            await finishMediaKit();
        } catch (e: unknown) {
            setError(e instanceof Error ? e.message : 'Failed to complete media kit');
        } finally {
            setLoading(false);
        }
    };

    /** Skip advances without saving; on the final highlight step it completes the kit. */
    const handleSkip = async () => {
        if (step === 8) {
            setLoading(true);
            setError(null);
            try {
                await finishMediaKit();
            } catch (e: unknown) {
                setError(e instanceof Error ? e.message : 'Failed to complete media kit');
            } finally {
                setLoading(false);
            }
            return;
        }
        if (step >= 2 && step < 8) {
            setStep((s) => (s + 1) as WizardStep);
        }
    };

    const gradYearEditable = !data?.graduationYear;
    const activeCategory = interestCategories[activeCategoryIndex];
    const photoCount = data?.photos.length ?? 0;
    const canAddPhoto = photoCount < MEDIA_KIT_MAX_PHOTOS;

    if (step === 0) {
        return (
            <div className="media-kit-wizard" data-testid="media-kit-landing">
                <div className="media-kit-landing">
                    <div className="media-kit-landing-cta">
                        <img
                            src={polarisStarLogo}
                            alt="Polaris"
                            className="media-kit-landing-logo"
                        />
                        <h2>Media Kit Builder</h2>
                        <p>Create your personalized media kit</p>
                        <button className="media-kit-btn-primary" onClick={handleGetStarted} data-testid="media-kit-get-started">
                            GET STARTED
                        </button>
                    </div>
                    <div className="media-kit-preview-card">
                        <img
                            src={mediaKitLandingPreview}
                            alt="Preview of a completed media kit"
                            className="media-kit-landing-preview-image"
                        />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="media-kit-wizard" data-testid={`media-kit-step-${step}`}>
            <button
                type="button"
                className="media-kit-btn-back"
                onClick={handleBack}
                disabled={loading}
                data-testid="media-kit-back"
            >
                BACK
            </button>

            {error && <div className="media-kit-error" style={{ textAlign: 'center', marginBottom: '1rem' }}>{error}</div>}

            {step === 1 && (
                <div className="media-kit-step">
                    <h2>The Basics</h2>
                    <p className="subtitle">Let&apos;s gather some info about you.</p>
                    <p style={{ fontWeight: 600, marginBottom: '0.25rem' }}>Here&apos;s what we know about you so far</p>
                    <p style={{ fontSize: '0.85rem', color: '#aaa', fontStyle: 'italic', marginBottom: '1.5rem' }}>
                        This information is created from your Athlete profile*
                    </p>
                    <div className="media-kit-field-group">
                        <label className="media-kit-field-label">First Name*</label>
                        <input className="media-kit-input" value={data?.firstName ?? ''} disabled />
                    </div>
                    <div className="media-kit-field-group">
                        <label className="media-kit-field-label">Last Name*</label>
                        <input className="media-kit-input" value={data?.lastName ?? ''} disabled />
                    </div>
                    <div className="media-kit-field-group">
                        <label className="media-kit-field-label">Graduation Year</label>
                        <input
                            className="media-kit-input"
                            value={graduationYear}
                            onChange={(e) => setGraduationYear(e.target.value)}
                            disabled={!gradYearEditable}
                            data-testid="media-kit-graduation-year"
                        />
                    </div>
                </div>
            )}

            {step === 2 && (
                <div className="media-kit-step">
                    <h2>About You</h2>
                    <p className="subtitle">
                        Share a bit about who you are. Think of it like this:<br />
                        &ldquo;I&apos;m a passionate [sport] player ([position]). What drives me: [motivation]. Outside sports, I love: [hobbies/interests]. Fun fact: [something unique].&rdquo;
                    </p>
                    <div className="media-kit-field-group">
                        <label className="media-kit-field-label">Type your about you description</label>
                        <textarea
                            className="media-kit-textarea media-kit-textarea-white"
                            value={aboutYou}
                            onChange={(e) => setAboutYou(truncateMediaKitField(e.target.value, MEDIA_KIT_MAX_ABOUT_CHARS))}
                            maxLength={MEDIA_KIT_MAX_ABOUT_CHARS}
                            data-testid="media-kit-about-you"
                        />
                    </div>
                </div>
            )}

            {step === 3 && (
                <div className="media-kit-step">
                    <h2>Add Photos</h2>
                    <p className="subtitle">
                        Upload up to {MEDIA_KIT_MAX_PHOTOS} photos that showcase who you are on and off the field.
                        Photos are optional. These are separate from your Athlete Profile photo.
                    </p>
                    <div className="media-kit-photo-grid">
                        {data?.photos.map((photo) => (
                            <div key={photo.id} className="media-kit-photo-item">
                                <img src={photo.imageUrl} alt="Media kit" />
                                <button type="button" className="media-kit-photo-remove" onClick={() => handleDeletePhoto(photo.id)}>×</button>
                            </div>
                        ))}
                    </div>
                    {canAddPhoto && (
                        <button type="button" className="media-kit-add-photo-btn" onClick={() => setShowUploadModal(true)} data-testid="media-kit-add-photo">
                            + Add Photo ({photoCount}/{MEDIA_KIT_MAX_PHOTOS})
                        </button>
                    )}
                    <UploadImageModal
                        open={showUploadModal}
                        onClose={() => setShowUploadModal(false)}
                        mode="immediate"
                        uploadUrl={buildMediaKitPhotoUploadUrl(athleteUserId)}
                        title="Upload media kit photo"
                        onUploaded={handlePhotoUploaded}
                    />
                </div>
            )}

            {step === 4 && (
                <div className="media-kit-step">
                    <h2>Social Outreach ✨</h2>
                    <p className="subtitle">
                        Here&apos;s what we&apos;ve gathered so far about your social channels. Do you have any additional channels you&apos;d like to share?
                    </p>
                    <div className="media-kit-social-edit">
                        <MediaKitSocialFields
                            values={socials}
                            errors={socialErrors}
                            onChange={(key, value) => {
                                setSocials((s) => ({ ...s, [key]: value }));
                                setSocialErrors((errors) => {
                                    const next = { ...errors };
                                    delete next[key];
                                    return next;
                                });
                            }}
                        />
                    </div>
                </div>
            )}

            {step === 5 && (
                <div className="media-kit-step media-kit-step-interests">
                    <h2>Share Your Interests ✨</h2>
                    <p className="subtitle">
                        Tell us a bit about what you love! Type keywords into the description box below—things like hobbies, passions, or anything that excites you.
                    </p>
                    {interestsLoading ? (
                        <p className="media-kit-interests-loading">Loading interests...</p>
                    ) : activeCategory ? (
                        <div className="media-kit-interest-category">
                            <h4>{activeCategory.name}</h4>
                            <p className="media-kit-interest-select-label">SELECT FROM THE FOLLOWING</p>
                            <div className="media-kit-interest-pills-row">
                                <div className="media-kit-interest-pills">
                                    {activeCategory.interests.map((interest) => (
                                        <button
                                            key={interest.id}
                                            type="button"
                                            className={`media-kit-interest-pill${selectedInterestIds.includes(interest.id) ? ' selected' : ''}`}
                                            onClick={() => toggleInterest(interest.id)}
                                            data-testid={`media-kit-interest-${interest.id}`}
                                        >
                                            {interest.name}
                                        </button>
                                    ))}
                                </div>
                                {interestCategories.length > 1 && (
                                    <button
                                        type="button"
                                        className="media-kit-interest-category-next"
                                        onClick={() => setActiveCategoryIndex((i) => (i + 1) % interestCategories.length)}
                                        aria-label="Next interest category"
                                        data-testid="media-kit-next-interest-category"
                                    >
                                        ▶
                                    </button>
                                )}
                            </div>
                        </div>
                    ) : (
                        <p className="media-kit-interests-empty">Interest options are unavailable right now. You can type yours below.</p>
                    )}
                    <div className="media-kit-field-group media-kit-interests-custom">
                        <label className="media-kit-field-label">Type your interests</label>
                        <input
                            className="media-kit-input media-kit-input-white"
                            value={customInterests}
                            onChange={(e) => setCustomInterests(e.target.value)}
                            data-testid="media-kit-custom-interests"
                        />
                    </div>
                </div>
            )}

            {step === 6 && (
                <div className="media-kit-step">
                    <h2>Record Your Intro Video 🎥</h2>
                    <p className="subtitle">Show us who you are! Paste a link to your intro video on YouTube or Vimeo.</p>
                    <div className="media-kit-field-group">
                        <label className="media-kit-field-label">Paste your video link</label>
                        <input
                            className="media-kit-input media-kit-input-white"
                            value={introVideoUrl}
                            onChange={(e) => { setIntroVideoUrl(e.target.value); setIntroVideoError(null); }}
                            data-testid="media-kit-intro-video"
                        />
                        {introVideoError && <span className="media-kit-error">{introVideoError}</span>}
                    </div>
                </div>
            )}

            {step === 7 && (
                <div className="media-kit-step media-kit-step-highlights">
                    <div className="media-kit-step-highlights-scroll">
                        <h2>Past Highlights</h2>
                        <p className="subtitle">Show off your achievements! Share brand ambassador experiences, sponsorships, or other highlights.</p>
                        <div className="media-kit-field-group">
                            <label className="media-kit-field-label">Title of opportunity</label>
                            <input
                                className="media-kit-input media-kit-input-white"
                                value={highlightTitle}
                                onChange={(e) => setHighlightTitle(truncateMediaKitField(e.target.value, MEDIA_KIT_MAX_HIGHLIGHT_TITLE_CHARS))}
                                maxLength={MEDIA_KIT_MAX_HIGHLIGHT_TITLE_CHARS}
                                data-testid="media-kit-highlight-title"
                            />
                        </div>
                        <div className="media-kit-field-group">
                            <label className="media-kit-field-label">Describe what you did</label>
                            <textarea
                                className="media-kit-textarea media-kit-textarea-white"
                                value={highlightDescription}
                                onChange={(e) => setHighlightDescription(truncateMediaKitField(e.target.value, MEDIA_KIT_MAX_HIGHLIGHT_DESCRIPTION_CHARS))}
                                maxLength={MEDIA_KIT_MAX_HIGHLIGHT_DESCRIPTION_CHARS}
                                data-testid="media-kit-highlight-description"
                            />
                        </div>
                        <div className="media-kit-field-group">
                            <label className="media-kit-field-label">Describe impact</label>
                            <textarea
                                className="media-kit-textarea media-kit-textarea-white"
                                value={highlightImpact}
                                onChange={(e) => setHighlightImpact(truncateMediaKitField(e.target.value, MEDIA_KIT_MAX_HIGHLIGHT_IMPACT_CHARS))}
                                maxLength={MEDIA_KIT_MAX_HIGHLIGHT_IMPACT_CHARS}
                                data-testid="media-kit-highlight-impact"
                            />
                        </div>
                    </div>
                </div>
            )}

            {step === 8 && (
                <div className="media-kit-step media-kit-step-highlights">
                    <div className="media-kit-step-highlights-scroll">
                        <h2>Add Additional Highlight</h2>
                        <p className="subtitle">Show off your achievements! Share any additional highlights you&apos;ve had so far.</p>
                        <div className="media-kit-field-group">
                            <label className="media-kit-field-label">Title of opportunity</label>
                            <input
                                className="media-kit-input media-kit-input-white"
                                value={additionalTitle}
                                onChange={(e) => setAdditionalTitle(truncateMediaKitField(e.target.value, MEDIA_KIT_MAX_HIGHLIGHT_TITLE_CHARS))}
                                maxLength={MEDIA_KIT_MAX_HIGHLIGHT_TITLE_CHARS}
                                data-testid="media-kit-additional-highlight-title"
                            />
                        </div>
                        <div className="media-kit-field-group">
                            <label className="media-kit-field-label">Describe what you did</label>
                            <textarea
                                className="media-kit-textarea media-kit-textarea-white"
                                value={additionalDescription}
                                onChange={(e) => setAdditionalDescription(truncateMediaKitField(e.target.value, MEDIA_KIT_MAX_HIGHLIGHT_DESCRIPTION_CHARS))}
                                maxLength={MEDIA_KIT_MAX_HIGHLIGHT_DESCRIPTION_CHARS}
                                data-testid="media-kit-additional-highlight-description"
                            />
                        </div>
                        <div className="media-kit-field-group">
                            <label className="media-kit-field-label">Describe impact</label>
                            <textarea
                                className="media-kit-textarea media-kit-textarea-white"
                                value={additionalImpact}
                                onChange={(e) => setAdditionalImpact(truncateMediaKitField(e.target.value, MEDIA_KIT_MAX_HIGHLIGHT_IMPACT_CHARS))}
                                maxLength={MEDIA_KIT_MAX_HIGHLIGHT_IMPACT_CHARS}
                                data-testid="media-kit-additional-highlight-impact"
                            />
                        </div>
                    </div>
                </div>
            )}

            <div className={`media-kit-wizard-actions${showUploadModal ? " media-kit-wizard-actions--hidden" : ""}`}>
                {step === 1 && (
                    <button className="media-kit-btn-next" onClick={handleNextBasics} disabled={loading} data-testid="media-kit-next">
                        NEXT
                    </button>
                )}
                {step === 2 && (
                    <button className="media-kit-btn-next" onClick={handleNextAbout} disabled={loading} data-testid="media-kit-next">
                        NEXT
                    </button>
                )}
                {step === 3 && (
                    <button className="media-kit-btn-next" onClick={handleNextPhotos} data-testid="media-kit-next">
                        NEXT
                    </button>
                )}
                {step === 4 && (
                    <button className="media-kit-btn-next" onClick={handleNextSocials} disabled={loading} data-testid="media-kit-next">
                        NEXT
                    </button>
                )}
                {step === 5 && (
                    <button className="media-kit-btn-next" onClick={handleNextInterests} disabled={loading} data-testid="media-kit-next">
                        NEXT
                    </button>
                )}
                {step === 6 && (
                    <button className="media-kit-btn-next" onClick={handleNextIntroVideo} disabled={loading} data-testid="media-kit-next">
                        NEXT
                    </button>
                )}
                {step === 7 && (
                    <button
                        className="media-kit-btn-next"
                        onClick={handleNextHighlight}
                        disabled={loading}
                        data-testid="media-kit-next"
                    >
                        NEXT
                    </button>
                )}
                {step === 8 && (
                    <button className="media-kit-btn-next" onClick={handleComplete} disabled={loading} data-testid="media-kit-complete">
                        NEXT
                    </button>
                )}
                {step >= 2 && step <= 8 && (
                    <button
                        type="button"
                        className="media-kit-btn-skip"
                        onClick={() => void handleSkip()}
                        disabled={loading}
                        data-testid="media-kit-skip"
                    >
                        SKIP
                    </button>
                )}
            </div>
        </div>
    );
}
